import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useColorMode } from '@chakra-ui/react';
import { SparkleStar } from '@peacecover/icon';
import { useGetMutualFollowers } from '../../../domains/user/ui/hooks/useGetMutualFollowers';
import { useCreateDMChatRoom } from '../../feed/hooks/useCreateDMChatRoom';
import useChatRoomsQuery from '../../../components/api/queries/useChatRoomsQuery';
import HorizontalScroll from '../../my-page/components/HorizontalScroll';
import ProfileAvatar from '../../my-page/components/ProfileAvatar';

interface FollowersProfileSectionProps {
  userProfileId: string;
}

const FollowersProfileSection = ({ userProfileId }: FollowersProfileSectionProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const { data: mutualFollowers } = useGetMutualFollowers();
  const { handleCreateDMChatRoom } = useCreateDMChatRoom({ userProfileId: userProfileId });

  return (
    <div className="mt-[32px] mb-[16px]">
      <p className="text-[15px] font-semibold leading-[18px] text-text-main-lv2 px-[24px]">
        {t('FollowersProfileSection.communicateWithBuddy')}
      </p>
      <HorizontalScroll className="mt-[15px]">
        <BettieProfileCard />
        <div className="flex items-center last:mr-[24px]">
          <div className="flex flex-col items-center gap-[8px] px-[6px] pt-[5px]">
            <button
              type="button"
              className="w-[66px] h-[66px] rounded-full bg-surface-lv2 flex items-center justify-center relative"
              onClick={() => {
                const redirectUrl = encodeURIComponent(`${location.pathname}${location.search}`);
                navigate(`/users/${userProfileId}/binder?redirectUrl=${redirectUrl}`);
              }}
            >
              <img
                src="/assets/icons/poca_binder_icon.png"
                alt="poca binder icon"
                className="w-[40px] h-[40px]"
              />
            </button>
            <p className="text-[13px] font-medium leading-[16px] text-text-main-lv2">
              {t('FollowersProfileSection.pocaBinder')}
            </p>
          </div>
          {mutualFollowers &&
            mutualFollowers.length > 0 &&
            mutualFollowers.slice(0, 3).map((user) => (
              <button
                type="button"
                key={user?.userProfileId}
                className="flex flex-col items-center gap-[8px] px-[6px] pt-[5px]"
                onClick={() => handleCreateDMChatRoom(user?.userProfileId)}
              >
                <ProfileAvatar
                  thumbnailUrl={user?.thumbnailUrl ?? ''}
                  widthAndHeight="66px"
                  svgWidthAndHeight="50px"
                  border="1px solid rgba(0,0,0,0.04)"
                  boxShadow="0 0 4px 0 rgba(0,0,0,0.08), 0 0 10px 0 rgba(0,0,0,0.22)"
                />
                <p className="text-[13px] font-medium leading-[16px] text-text-main-lv2 w-[68px] truncate text-center">
                  {user?.nickname}
                </p>
              </button>
            ))}
          {/* <div className="flex flex-col items-center gap-[8px] px-[6px] pt-[5px]">
            <button
              type="button"
              className="w-[66px] h-[66px] rounded-full bg-surface-lv2 flex items-center justify-center"
              onClick={() => navigate(hasBuddyProfileCard ? '/fan-buddy/find' : '/fan-buddy/intro')}
            >
              <img
                src="/assets/icons/fan_buddy_icon.png"
                alt="fan buddy icon"
                className="w-[39px] h-[38px]"
              />
            </button>
            <p className="text-[13px] font-medium leading-[16px] text-text-main-lv2">
              {t('FollowersProfileSection.fanBuddy')}
            </p>
          </div> */}
          <div className="flex flex-col items-center gap-[8px] px-[6px] pt-[5px]">
            <button
              type="button"
              className="w-[66px] h-[66px] rounded-full bg-surface-lv2 flex items-center justify-center relative"
              onClick={() => navigate('/free-charge/invite')}
            >
              <img
                src="/assets/icons/invite_friends_icon.png"
                alt="invite friends icon"
                className="w-[40px] h-[40px]"
              />
              <div className="absolute bottom-[-3px] left-1/2 -translate-x-1/2 py-[2px] pl-[5px] pr-[4px] rounded-full text-[8px] font-semibold leading-[10px] text-pure-white bg-[linear-gradient(90deg,#458FFF_0%,#CF6AFF_100%)] animate-gradient">
                EVENT
              </div>
            </button>
            <p className="text-[13px] font-medium leading-[16px] text-text-main-lv2">
              {t('FollowersProfileSection.inviteFriends')}
            </p>
          </div>
        </div>
      </HorizontalScroll>
    </div>
  );
};

export default FollowersProfileSection;

const BettieProfileCard = () => {
  const navigate = useNavigate();
  const { colorMode } = useColorMode();
  const isDarkMode = colorMode === 'dark';
  const { data, isFetching } = useChatRoomsQuery(
    {
      type: 'All',
      tradingPhotoCardId: undefined,
      onlyJoined: true,
      onlyBettie: true
    },
    {
      refetchOnWindowFocus: false
    }
  );
  const bettieChatRooms = useMemo(
    () => data?.pages?.flatMap((page) => page.chatRooms) ?? [],
    [data]
  );

  const randomBettieChatRoom = useMemo(() => {
    if (bettieChatRooms.length === 0) return undefined;
    return bettieChatRooms[Math.floor(Math.random() * bettieChatRooms.length)];
  }, [bettieChatRooms]);

  const existingBettieChatRoom = bettieChatRooms?.length > 0;

  return (
    <div className="flex flex-col items-center gap-[8px] ml-[13px] px-[7px] pt-[5px]">
      <button
        type="button"
        className="relative"
        disabled={isFetching}
        onClick={() => navigate(existingBettieChatRoom ? '/bettie-chat-list' : '/bettie-setting')}
      >
        <SparkleStar
          size={14}
          className="text-[#CDC3FF] absolute top-[-1px] left-[-4px] bettie-sparkle"
          style={{ animationDelay: '0s' }}
        />
        <SparkleStar
          size={9}
          className="text-[#B2A3FF] absolute top-[-5px] left-[8px] bettie-sparkle"
          style={{ animationDelay: '0.9s' }}
        />
        <SparkleStar
          size={14}
          className="text-[#CDC3FF] absolute bottom-[-2px] right-[-3px] bettie-sparkle"
          style={{ animationDelay: '1.8s' }}
        />
        <SparkleStar
          size={9}
          className="text-[#B2A3FF] absolute bottom-[-6px] right-[9px] bettie-sparkle"
          style={{ animationDelay: '2.7s' }}
        />
        <div className="relative h-[68px] w-[68px] rounded-full overflow-hidden">
          <div className="absolute inset-0 bg-gradient-multi-rainbox-lv2" />
          <div
            className="absolute inset-0 z-[10] rounded-full"
            style={{
              background:
                'linear-gradient(0deg, #FFF 0%, #FFF 100%), radial-gradient(50% 50% at 50% 50%, rgba(255, 255, 255, 0.88) 0%, rgba(255, 255, 255, 0.00) 100%), conic-gradient(from 270deg at 50% 50%, rgba(148, 165, 249, 0.64) 0deg, rgba(92, 219, 246, 0.64) 108.00000429153442deg, rgba(255, 225, 117, 0.64) 180deg, rgba(214, 113, 246, 0.64) 288.0000042915344deg, rgba(159, 157, 249, 0.64) 360deg)',
              filter: isDarkMode ? 'blur(17.2px)' : 'blur(17.5px)',
              clipPath: 'circle(50% at 50% 50%)',
              WebkitClipPath: 'circle(50% at 50% 50%)'
            }}
          />
          <div className="absolute top-1/2 left-1/2 z-20 bettie-orbit">
            <div className="bettie-orbit-item">
              <div className="relative w-[46px] h-[46px] rounded-full overflow-hidden shrink-0">
                <img
                  src={
                    randomBettieChatRoom?.bettieProfile?.thumbnailUrl ??
                    '/assets/bettie/bettie_empty_profile.png'
                  }
                  alt="bettie"
                  className="h-[46px] w-[46px] rounded-full object-cover"
                />
                <img
                  src="/assets/icons/profile_bubble.png"
                  alt="profile bubble"
                  className="absolute inset-0 w-full h-full object-fill z-10 pointer-events-none"
                />
              </div>
            </div>
          </div>
        </div>
      </button>
      <p className="text-[13px] font-medium leading-[16px] text-text-main-lv2">Bettie</p>
    </div>
  );
};
